'use client';

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { encodeFunctionData, type Hex } from 'viem';
import { LogOut } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { useTransactionQueue } from '@/hooks/use-transactions';
import { useWallet } from '@/hooks/use-wallet';
import { YIELD_ADAPTER_WITHDRAW_ABI } from '@/lib/contracts';
import { LobbyYieldPanelActionButton } from '@/features/lobby/panels/adapters/lobby-yield-panel-action';

const withdrawLpTooltipSurface =
  'max-w-[260px] rounded-[6px] border border-amber-500/28 bg-black/95 backdrop-blur-md shadow-lg px-2.5 py-2 text-[10px] leading-snug text-amber-50/95 tracking-[0.06em]';

export function CreatorWithdrawLpButton({
  assetHash,
  adapterAddress,
  ownsAllTiles,
  viewerAddress,
  liveGridCompositeId,
}: {
  assetHash: `0x${string}`;
  adapterAddress: `0x${string}`;
  ownsAllTiles: boolean;
  viewerAddress: string | null | undefined;
  liveGridCompositeId: string;
}) {
  const { executeActions } = useTransactionQueue();
  const queryClient = useQueryClient();
  const { wallet } = useWallet();
  const [isPending, setIsPending] = useState(false);

  const canSubmit = !!(wallet && viewerAddress && ownsAllTiles && liveGridCompositeId);

  const onWithdraw = useCallback(async () => {
    if (!viewerAddress || isPending || !ownsAllTiles) return;
    setIsPending(true);
    try {
      await executeActions(
        'Withdraw LP position',
        [
          {
            id: `withdraw-${assetHash.slice(0, 10)}`,
            label: 'Withdraw LP',
            description: 'Return yield position from adapter to your wallet',
            call: {
              to: adapterAddress,
              data: encodeFunctionData({
                abi: YIELD_ADAPTER_WITHDRAW_ABI,
                functionName: 'withdraw',
                args: [assetHash, viewerAddress as Hex],
              }),
            },
          },
        ],
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: ['v2-grid', liveGridCompositeId],
            });
          },
        },
      );
    } finally {
      setIsPending(false);
    }
  }, [
    adapterAddress,
    assetHash,
    executeActions,
    isPending,
    liveGridCompositeId,
    ownsAllTiles,
    queryClient,
    viewerAddress,
  ]);

  const button = (
    <LobbyYieldPanelActionButton
      tone="amber"
      icon={LogOut}
      label="Withdraw LP"
      disabled={isPending || !canSubmit}
      pending={isPending}
      onClick={onWithdraw}
    />
  );

  if (!viewerAddress || !ownsAllTiles) {
    const tooltipCopy = !viewerAddress
      ? 'Connect your wallet to withdraw LP from this adapter.'
      : 'You must hold every seat on this board to withdraw LP.';
    return (
      <Tooltip disableTouch>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex w-full">
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent sideOffset={6} className={withdrawLpTooltipSurface}>
          {tooltipCopy}
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
}
